import Blueprint from "$blueprint/js/blueprint";
import Reveal from "$blueprint/js/reveal/index";
import Forms from "$blueprint/js/forms/index";
import Swiper from "$blueprint/js/swiper/index";

import Axios from "axios";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import LocomotiveScroll from "locomotive-scroll";
import ImagesLoaded from "imagesloaded";

require("./throttle");
require("./alpine/link-animated.js");
require("./alpine/button-next-section.js");
require("./alpine/enquiry-form.js");

// Normally not recommended.
// Once page is loaded, load remaining lazyload images.
// We're doing this to fix the following problem:
// Images down the page loading in are triggering reflow, meaning we manually update locomotive and scroll trigger
// for these events too. This is very costly and signficiantly inturupts scrolling for the user.
// By preloading lazyload images, there's no costly reflow on scroll.
//
// HOWEVER
//
// This knocks about 60% of the benefit from using lazysizes: "don't load images the user never chooses to view".
//
// TODO: Proposed fix (big): provide option for `@livewire('content.image', [...])` to show a same-sized div until image is lazyloaded.
// This will stop the whole webpage reflowing in size, removing the need to update locomotive and scroll trigger with images are loaded.
window.lazySizes.cfg.preloadAfterLoad = true;

window.axios = Axios;

// Manually overriding our normal Swup container as Locomotive scroll needs to sit above it in DOM.
const blueprintConfiguration = {
    swup: {
        containers: ["#swup"],
    },
};

Blueprint(blueprintConfiguration).then(blueprint => {

    // show fickity on CMS and website. Only setup SWUP on website.
    Swiper.create().then(() => {
        if (blueprint.isWebsite){
            Swiper.swup(blueprint.swup);
        }
    });

    if (blueprint.isWebsite){
        Reveal.create().then(Reveal.swup(blueprint.swup));
        Forms.create().then(Forms.swup(blueprint.swup));

        gsap.registerPlugin(ScrollTrigger);

        const swupPagesScrollPositionCache = {};

        var elementScrollContainer = null;
        var scroll = null;
        var hasHorizontalScroll = false;

        // Tag all cloaked elements on first page load.
        document.body.querySelectorAll('[x-cloak]').forEach((node) => {
            node.setAttribute('data-alpine-was-cloaked', '');
        })

        const initAlpine = () => {
            window.Alpine.discoverUninitializedComponents((el) => {
                window.Alpine.initializeComponent(el);
            });

            requestAnimationFrame(() => { window.Alpine.pauseMutationObserver = false });
        };

        const beforeRenderAlpine = (event) => {
            const newBody = document.body;
            newBody.querySelectorAll('[data-alpine-generated-me],[x-cloak]').forEach((el) => {
                if (el.hasAttribute('x-cloak')) {
                    // When we get a new document body tag any cloaked elements so we can cloak
                    // them again before caching.
                    el.setAttribute('data-alpine-was-cloaked', '')
                }

                if (el.hasAttribute('data-alpine-generated-me')) {
                    el.removeAttribute('data-alpine-generated-me')
                    if (typeof el.__x_for_key === 'undefined' && typeof el.__x_inserted_me === 'undefined') {
                        el.remove()
                    }
                }
            })
        };

        const beforeCacheAlpine = () => {
            window.Alpine.pauseMutationObserver = true;

            document.body.querySelectorAll('[x-for],[x-if],[data-alpine-was-cloaked]').forEach((el) => {
                // Cloak any elements again that were tagged when the page was loaded
                if (el.hasAttribute('data-alpine-was-cloaked')) {
                    el.setAttribute('x-cloak', '')
                    el.removeAttribute('data-alpine-was-cloaked')
                }

                if (el.hasAttribute('x-for')) {
                    let nextEl = el.nextElementSibling
                    while (nextEl && typeof nextEl.__x_for_key !== 'undefined') {
                        const currEl = nextEl
                        nextEl = nextEl.nextElementSibling
                        currEl.setAttribute('data-alpine-generated-me', true)
                    }
                } else if (el.hasAttribute('x-if')) {
                    const ifEl = el.nextElementSibling
                    if (ifEl && typeof ifEl.__x_inserted_me !== 'undefined') {
                        ifEl.setAttribute('data-alpine-generated-me', true)
                    }
                }
            })
        };

        const createScroll = () => {
            elementScrollContainer = document.querySelector('#page');

            if (elementScrollContainer == null){
                return;
            }

            if (scroll == null){
                scroll = window.scroll = new LocomotiveScroll({
                    el: elementScrollContainer,
                    smooth: true,
                    reloadOnContextChange: true,
                    // tablet: {
                    //     smooth: true,
                    // },
                    // mobile: {
                    //     smooth: true,
                    // },
                    // smartphone: {
                    //     smooth: true,
                    // }
                });

                scroll.on("scroll", ScrollTrigger.update);
                scroll.on("resize", ScrollTrigger.refresh);

                window.addEventListener("resize", () => {
                    ScrollTrigger.update();
                    ScrollTrigger.refresh();
                });

                ScrollTrigger.scrollerProxy(elementScrollContainer, {
                    scrollTop: (value) => {
                        if (scroll){
                            return value != null ? scroll.scrollTo(value, 0, 0) : scroll.scroll.instance.scroll.y;
                        }
                        return window.scrollY;
                    },
                    getBoundingClientRect: () => {
                        return {
                            left: 0,
                            top: 0,
                            width: window.innerWidth,
                            height: window.innerHeight
                        };
                    },
                    pinType: elementScrollContainer.style.transform != "" ? "transform" : "fixed"
                });

                ScrollTrigger.addEventListener("refresh", () => scroll.update());
            }
            else {
                scroll.update();
            }

            const imagesLoaded = new ImagesLoaded(elementScrollContainer);
            imagesLoaded.on("progress", () => {
                Swiper.instances.forEach(instance => {
                    instance.swiper.updateSize();
                });
            });

            elementScrollContainer.addEventListener("done", event => {
                scroll.update();
            });

            const elementHorizontalScroll = document.querySelector("#horizontal-scroll");

            hasHorizontalScroll = elementHorizontalScroll != null;

            ScrollTrigger.matchMedia({
                "(min-width: 1024px)": function()
                {
                    if (hasHorizontalScroll){
                        gsap.to(".pin-wrap", {
                            scrollTrigger: {
                                scroller: elementScrollContainer, //locomotive-scroll
                                scrub: true,
                                trigger: "#horizontal-scroll",
                                pin: true,
                                // anticipatePin: 1,
                                start: "top top",
                                end(){
                                    var pinWrap = document.querySelector(".pin-wrap");
                                    var pinWrapWidth = pinWrap.offsetWidth;
                                    return pinWrapWidth;
                                },
                                invalidateOnRefresh: true,
                            },
                            x(){
                                var pinWrap = document.querySelector(".pin-wrap");
                                var pinWrapWidth = pinWrap.offsetWidth;
                                var horizontalScrollLength = pinWrapWidth - window.innerWidth;

                                return -horizontalScrollLength;
                            },
                            ease: "none"
                        });

                        ScrollTrigger.refresh();
                    }
                },
            });
        };

        createScroll();

        document.addEventListener("lazyloaded", event => {
            if (scroll){
                scroll.update();
                requestAnimationFrame(() => {
                    ScrollTrigger.refresh();
                    requestAnimationFrame(() => {
                        Swiper.instances.forEach(instance => {
                            instance.swiper.updateSize();
                        });
                    });
                });
            }
        });

        blueprint.swup.on("clickLink", (event) => {

            // If the clicked link is in our scroll position cache, clear it.
            // We only want this cache used when user is paginating through their history
            // (i.e. back and forward history buttons)
            try {
                const delegatePathname = event.delegateTarget.pathname;

                if (swupPagesScrollPositionCache[delegatePathname]){
                    delete swupPagesScrollPositionCache[delegatePathname];
                }
            } catch (error){
                console.log("swup clickLink failed to find deleteTarget pathname of URL. Error: ", error);
            }

            // Cache the scroll position of page we're leaving, so if user goes back they don't
            // have to scroll back down.
            swupPagesScrollPositionCache[window.location.pathname] = scroll.scroll.instance.scroll.y;
        });

        blueprint.swup.on("contentReplaced", () => {
            createScroll();
            // ScrollTrigger.refresh();
            beforeRenderAlpine();
            beforeCacheAlpine();
            initAlpine();
            scroll.update();

            if (swupPagesScrollPositionCache[window.location.pathname]){
                scroll.scrollTo(swupPagesScrollPositionCache[window.location.pathname]);
            }

            // scroll trigger needs a moment to register
            setTimeout(() => {
                ScrollTrigger.refresh();
            }, 100);
        });

        blueprint.swup.on("animationOutDone", event => {
            gsap.killTweensOf(".pin-wrap");

            ScrollTrigger.getAll().forEach(instance => {
                instance.kill();
            });
            scroll.scrollTo("top", {
                duration: "1",
                disableLerp: true,
            });
        });

        blueprint.swup.on("popState", event => {
            gsap.killTweensOf(".pin-wrap");

            ScrollTrigger.getAll().forEach(instance => {
                instance.kill();
            });
            scroll.scrollTo("top", {
                duration: "1",
                disableLerp: true,
            });

            initAlpine();
        });

        const contactUrl = window.location.origin + "/contact-us";
        const styles = "font-size: 1.25rem; color: #3c766a;";
        // rickroll image
        console.log("%c  ", "background-image: url(https://i.imgur.com/G5wIFwD.gif); background-size: 8rem;background-repeat:no-repeat;font-size: 8rem;");
        console.log("%cHello there keen developer. We're often looking for new talent, why not get in touch with us? Attach code '#i_saw_the_console'. 👋 \n" + contactUrl + "\n", styles);
    }
});
